import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Usuario, Rol, Permiso } from '../types/usuario.types';

interface UsuarioStore {
  // State
  usuarios: Usuario[];
  roles: Rol[];
  permisos: Permiso[];
  loading: boolean;
  error: string | null;

  // Actions - Usuarios
  setUsuarios: (usuarios: Usuario[]) => void;
  addUsuario: (usuario: Usuario) => void;
  updateUsuario: (usuario: Usuario) => void;
  removeUsuario: (id: number) => void;

  // Actions - Roles
  setRoles: (roles: Rol[]) => void;
  addRol: (rol: Rol) => void;
  updateRol: (rol: Rol) => void;
  removeRol: (id: number) => void;

  // Actions - Permisos
  setPermisos: (permisos: Permiso[]) => void;
  addPermiso: (permiso: Permiso) => void;
  updatePermiso: (permiso: Permiso) => void;
  removePermiso: (id: number) => void;

  // Actions - Common
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useUsuarioStore = create<UsuarioStore>()(
  devtools(
    (set) => ({
      // Initial state
      usuarios: [],
      roles: [],
      permisos: [],
      loading: false,
      error: null,

      // Actions - Usuarios
      setUsuarios: (usuarios) => set({ usuarios }),
      addUsuario: (usuario) =>
        set((state) => ({ usuarios: [...state.usuarios, usuario] })),
      updateUsuario: (usuario) =>
        set((state) => ({
          usuarios: state.usuarios.map((u) =>
            u.id === usuario.id ? usuario : u
          )
        })),
      removeUsuario: (id) =>
        set((state) => ({
          usuarios: state.usuarios.filter((u) => u.id !== id)
        })),

      // Actions - Roles
      setRoles: (roles) => set({ roles }),
      addRol: (rol) =>
        set((state) => ({ roles: [...state.roles, rol] })),
      updateRol: (rol) =>
        set((state) => ({
          roles: state.roles.map((r) => (r.id === rol.id ? rol : r))
        })),
      removeRol: (id) =>
        set((state) => ({
          roles: state.roles.filter((r) => r.id !== id)
        })),

      // Actions - Permisos
      setPermisos: (permisos) => set({ permisos }),
      addPermiso: (permiso) =>
        set((state) => ({ permisos: [...state.permisos, permiso] })),
      updatePermiso: (permiso) =>
        set((state) => ({
          permisos: state.permisos.map((p) =>
            p.id === permiso.id ? permiso : p
          )
        })),
      removePermiso: (id) =>
        set((state) => ({
          permisos: state.permisos.filter((p) => p.id !== id)
        })),

      // Actions - Common
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null })
    }),
    { name: 'UsuarioStore' }
  )
);
